import React, { forwardRef, CSSProperties } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import styles from "./Flex.scss";

export type FlexProps = React.PropsWithChildren<{
  customClass?: string;
  container?: boolean;
  wrap?: boolean;
  gap?: boolean | string;
}> &
  Omit<CSSProperties, "gap">;

// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
export const Flex = forwardRef<HTMLDivElement, FlexProps>(
  (props, forwardedRef) => {
    const {
      children,
      width,
      height,
      margin,
      padding,
      justifyContent,
      flexDirection,
      flexGrow,
      flexBasis,
      flexShrink,
      flex,
      alignItems,
      container,
      wrap,
      customClass,
      gap,
      ...otherStyles
    } = props;
    const flexStyles = {
      width,
      height,
      margin,
      padding,
      justifyContent,
      flexDirection,
      flex,
      flexGrow,
      flexBasis,
      flexShrink,
      flexWrap: wrap ? "wrap" : "nowrap",
      gap: typeof gap === "string" ? gap : undefined,
      alignItems,
      ...otherStyles,
    } as const;

    const classes = classNames(
      styles.flex,
      {
        [styles.container]: container,
        [styles.gap]: gap === true,
      },
      customClass
    );
    return (
      <div ref={forwardedRef} style={flexStyles} className={classes}>
        {children}
      </div>
    );
  }
);

Flex.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  customClass: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  justifyContent: PropTypes.string,
  flexDirection: PropTypes.oneOf([
    "-moz-initial",
    "inherit",
    "initial",
    "revert",
    "unset",
    "column",
    "column-reverse",
    "row",
    "row-reverse",
  ]),
  flexGrow: PropTypes.number,
  flexBasis: PropTypes.string,
  flexShrink: PropTypes.number,
  flex: PropTypes.string,
  alignItems: PropTypes.string,
  container: PropTypes.bool,
  wrap: PropTypes.bool,
  gap: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

Flex.defaultProps = {
  customClass: undefined,
  children: null,
  width: undefined,
  height: undefined,
  margin: undefined,
  padding: undefined,
  justifyContent: undefined,
  flexDirection: undefined,
  flexGrow: undefined,
  flexBasis: undefined,
  flexShrink: undefined,
  flex: undefined,
  alignItems: undefined,
  container: false,
  wrap: false,
  gap: false,
};

Flex.displayName = "Flex";

export default Flex;
